'use client';

import type { ProductToCompareT } from '../../../../type/product/productComparison';

import {
  ProductComparisonBar,
  ProductComparisonBarCell,
  ProductComparisonBarCopy,
  ProductComparisonBarMedia,
  ProductComparisonBarName,
  ProductComparisonBarPrice,
  ProductComparisonBarScroller,
  ProductComparisonBarVariant,
  ProductComparisonGrid,
  ProductComparisonGridVariant,
  productComparisonColumnStyle,
  productComparisonImageClasses,
} from '@vs-shop/commerce-ui/productComparison';
import { Container } from '@vs-shop/ui/container';
import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';

import { getDefaultCurrency } from '../../../../config/currency';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';

import { productComparisonDrag, productComparisonDrop } from './dragState';

import VsImage from '@vs-shop/image-link/Image';
import Link from '@vs-shop/image-link/Link';
import { IntlMoney } from '../../../server/intl/Intl';

import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

type Props = {
  products: readonly ProductToCompareT[];
  isVisible: boolean;
  draggingIndex: number | null;
  dragOverIndex: number | null;
};

export default function ProductComparisonStickyHeader({
  products,
  isVisible,
  draggingIndex,
  dragOverIndex,
}: Props) {
  const currency = getDefaultCurrency();

  return (
    <ProductComparisonBar
      variant={isVisible ? ProductComparisonBarVariant.RAISED : ProductComparisonBarVariant.SETTLED}
    >
      <Container classNameWrapper="bg-background-intense">
        <ProductComparisonBarScroller>
          <ProductComparisonGrid
            variant={ProductComparisonGridVariant.BAR}
            style={productComparisonColumnStyle(products.length)}
          >
            {/* Empty first column */}
            <div />

            {/* Product columns */}
            {products.map((product, index) => {
              const imagePathAbstract = product.imagePath ?? null;
              const imagePathResolved =
                imagePathAbstract !== null
                  ? resolveImageUrl(imagePathAbstract, ImageFilter.product_comparison_sticky_header)
                  : null;

              return (
                <ProductComparisonBarCell
                  key={`sticky-${product.code}`}
                  drag={productComparisonDrag(draggingIndex, index)}
                  drop={productComparisonDrop(dragOverIndex, index)}
                >
                  {/* Image */}
                  <Link href={product.routerUrl} title={product.name} className="flex shrink-0">
                    <ProductComparisonBarMedia>
                      {imagePathResolved !== null ? (
                        <VsImage
                          src={imagePathResolved}
                          alt={product.name}
                          width={107}
                          height={61}
                          className={productComparisonImageClasses}
                        />
                      ) : (
                        <ImageMissingPlaceholder />
                      )}
                    </ProductComparisonBarMedia>
                  </Link>

                  {/* Name and Price */}
                  <ProductComparisonBarCopy>
                    <ProductComparisonBarName
                      render={<Link href={product.routerUrl} title={product.name} />}
                    >
                      {product.name}
                    </ProductComparisonBarName>
                    <ProductComparisonBarPrice>
                      <IntlMoney value={{ amount: product.price, currency }} />
                    </ProductComparisonBarPrice>
                  </ProductComparisonBarCopy>
                </ProductComparisonBarCell>
              );
            })}
          </ProductComparisonGrid>
        </ProductComparisonBarScroller>
      </Container>
    </ProductComparisonBar>
  );
}
